import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { FileText, Download, Image as ImageIcon, FileType } from 'lucide-react';

const CommentRenderer = ({ text, members = [], prefix = true, attachments = [] }) => {
  // Preprocess mentions: convert @user_id to markdown link with mention:// protocol
  const preprocessMentions = (input) => {
    if (!input) return "";
    const regex = prefix === false
      ? /([0-9a-fA-F-]{36})/g
      : /@([0-9a-fA-F-]{36})/g;

    return input.replace(regex, (match, userId) => {
      const member = members.find((m) => m.user_id === userId);
      if (member) {
        const display = prefix === false ? member.user_name : `@${member.user_name}`;
        return `[${display}](mention://${userId})`;
      }
      return match;
    });
  };

  const processedText = preprocessMentions(text);

  const isImage = (fileType) => fileType?.startsWith('image/');
  const isPDF = (fileType) => fileType === 'application/pdf';

  return (
    <div className="w-full">
      <div className="prose prose-sm max-w-none whitespace-normal break-words">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children, ...props }) => {
              if (href?.startsWith("mention://")) {
                const userId = href.replace("mention://", "");
                const member = members.find((m) => m.user_id === userId);
                return (
                  <span
                    className="text-blue-600 underline cursor-pointer"
                    title={member ? `${member.user_name} (${member.user_email})` : userId}
                    {...props}
                  >
                    {children}
                  </span>
                );
              }
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                  {...props}
                >
                  {children}
                </a>
              );
            },
            p: ({ children, ...props }) => (
              <p className="whitespace-normal break-words mb-2 last:mb-0" {...props}>
                {children}
              </p>
            ),
            ul: ({ children, ...props }) => (
              <ul className="list-disc pl-5 mb-2 last:mb-0" {...props}>{children}</ul>
            ),
            ol: ({ children, ...props }) => (
              <ol className="list-decimal pl-5 mb-2 last:mb-0" {...props}>{children}</ol>
            ),
            li: ({ children, ...props }) => (
              <li className="mb-0.5" {...props}>{children}</li>
            ),
            h1: ({ children, ...props }) => (
              <h1 className="text-lg font-bold mb-2 mt-3" {...props}>{children}</h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 className="text-base font-bold mb-2 mt-3" {...props}>{children}</h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 className="text-sm font-bold mb-1 mt-2" {...props}>{children}</h3>
            ),
            code: ({ children, inline, ...props }) => {
              if (inline) {
                return (
                  <code className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-xs" {...props}>
                    {children}
                  </code>
                );
              }
              return (
                <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto text-xs mb-2">
                  <code className="text-gray-800" {...props}>{children}</code>
                </pre>
              );
            },
            blockquote: ({ children, ...props }) => (
              <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-600 mb-2" {...props}>
                {children}
              </blockquote>
            ),
            table: ({ children, ...props }) => (
              <div className="overflow-x-auto mb-2">
                <table className="min-w-full border-collapse border border-gray-200 text-xs" {...props}>
                  {children}
                </table>
              </div>
            ),
            th: ({ children, ...props }) => (
              <th className="border border-gray-200 px-2 py-1 bg-gray-50 font-semibold text-left" {...props}>
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td className="border border-gray-200 px-2 py-1" {...props}>
                {children}
              </td>
            ),
          }}
        >
          {processedText}
        </ReactMarkdown>
      </div>

      {/* Attachments */}
      {attachments && attachments.length > 0 && (
        <div className="mt-2 space-y-2">
          {attachments.map((file, index) => (
            <div key={index} className="border rounded-lg overflow-hidden">
              {isImage(file.file_type) ? (
                // Image preview
                <a href={file.file_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={file.file_url}
                    alt={file.file_name}
                    className="max-w-full h-auto max-h-64 object-contain cursor-pointer hover:opacity-90"
                  />
                </a>
              ) : isPDF(file.file_type) ? (
                // PDF preview
                <div className="bg-gray-50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileType className="w-5 h-5 text-red-500" />
                      <span className="text-sm font-medium">{file.file_name}</span>
                    </div>
                    <a
                      href={file.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                  <iframe
                    src={file.file_url}
                    className="w-full h-64 border-0"
                    title={file.file_name}
                  />
                </div>
              ) : (
                // File download
                <a
                  href={file.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <FileText className="w-5 h-5 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.file_name}</p>
                    {file.file_size && (
                      <p className="text-xs text-gray-500">
                        {(file.file_size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </div>
                  <Download className="w-4 h-4 text-gray-400" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentRenderer;
